import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Heading, InfoCard } from "@cividao/components"
import { DomainError } from "../../../../domain/_kernel/DomainError"
import { ErrorCodes } from "../../../../domain/_kernel/ErrorCodes"
import { LogoutModal } from "../../../modules/LogoutModal"
import { useLogos } from "../../../../hooks/useLogos"
import styles from "../index.module.css"

const AUTH_ERROR_CODES: ErrorCodes[] = [
  ErrorCodes.USER_NOT_AUTHENTICATED,
  ErrorCodes.USER_NOT_GRANTED,
  ErrorCodes.USER_NOT_GRANTED_TO_VIEW_INFO,
]

interface DefaultErrorProps {
  error: { status?: number; message?: string } | null
}

export function DefaultError({ error }: DefaultErrorProps) {
  const { t } = useTranslation(["error", "auth", "profile"])
  const { logoFull: logoSrc } = useLogos()
  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const UNKNOWN_ERROR = {
    title: t("title"),
    subtitle: t("description"),
  }

  const NOT_FOUND_ERROR = {
    title: t("notFound.title"),
    subtitle: t("notFound.description"),
  }

  const WALLET_MISMATCH_ERROR = {
    title: t("walletMismatch.title"),
    subtitle: t("walletMismatch.description"),
  }

  const ErrorMessagesByHTTPCode: { [key: number]: { title: string; subtitle: string } } = {
    403: WALLET_MISMATCH_ERROR,
    404: NOT_FOUND_ERROR,
    500: UNKNOWN_ERROR,
  }

  const isDomainError = error instanceof DomainError
  const errorStatus = isDomainError ? 400 : (error?.status ?? 500)
  const errorMessage = isDomainError
    ? String(t(`auth:errors.${error.code}`, { defaultValue: t("auth:errors.unknown") }))
    : error?.message
  const errorContext = ErrorMessagesByHTTPCode[errorStatus] ?? UNKNOWN_ERROR
  const { title, subtitle } = errorContext

  const isAuthError = isDomainError && AUTH_ERROR_CODES.includes(error.code as ErrorCodes)

  const handleBackHome = () => {
    window.location.href = "/"
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <img className={styles.image} src={logoSrc} />
        <div>
          <Heading as="h2" align="center">
            {title}
          </Heading>
          <p className={styles.message}>{subtitle}</p>
        </div>
        {errorMessage && <InfoCard dismissable={false} variant="error" title="Error" message={errorMessage} />}
        <Button isFull onClick={handleBackHome}>
          {t("backHome")}
        </Button>
        {isAuthError && (
          <Button isFull type="outline" onClick={() => setShowLogoutModal(true)}>
            {t("disconnect")}
          </Button>
        )}
      </div>

      {showLogoutModal && (
        <LogoutModal
          onClose={() => setShowLogoutModal(false)}
          title={t("profile:disconnectModal.title")}
          message={t("disconnectMessage")}
          email=""
          confirmText={t("profile:disconnectModal.confirm")}
          cancelText={t("profile:disconnectModal.cancel")}
          showLogo={false}
        />
      )}
    </div>
  )
}
